import { useMemo, useRef, useState } from 'react';
import { Drawer, Segmented } from '../../components/data';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type {
  OnboardingCategory,
  OnboardingOption,
  OnboardingTemplate,
  OnboardingTemplateFile,
} from '../../lib/types';
import {
  CATEGORIES,
  CATEGORY_GLYPH,
  CATEGORY_LABEL,
  fillPlaceholders,
  formatBytes,
  mailtoHref,
} from './onboardingMeta';
import { downloadTemplateFile, readAsBase64, saveDraftToMailbox } from './onboardingEmail';

/**
 * The task library — the reusable tasks every onboarding is built from.
 *
 * Grouped by category rather than listed flat, because the categories are what
 * the trigger rules sweep: "managed IT pulls in all of setup" only makes sense
 * to a reader who can see what is in setup.
 *
 * What each entry shows that the source app's library did not is WHAT PULLS IT
 * IN. A library of forty tasks with no visible rules is forty tasks nobody
 * dares change, because there is no way to tell which client gets which.
 */

const EMPTY = {
  title: '',
  description: '',
  category: 'setup' as OnboardingCategory,
  defaultDueDays: 7,
  requiresUpload: false,
  uploadLabel: '',
  emailSubject: '',
  emailBody: '',
  internalNotes: '',
  sortOrder: 0,
};

type Scope = 'active' | 'retired';

export function OnboardingLibrary() {
  const [scope, setScope] = useState<Scope>('active');
  /* The URL is the cache key in useResource, so switching scope refetches
     rather than filtering a list that never held the retired rows. */
  const templates = useResource<OnboardingTemplate[]>(
    `/sales/onboarding/templates?scope=${scope}`,
  );
  const options = useResource<OnboardingOption[]>('/sales/onboarding/options');
  const [editing, setEditing] = useState<OnboardingTemplate | 'new' | null>(null);
  const [composing, setComposing] = useState<OnboardingTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** Service codes read as names — `managed-it` means nothing on a card. */
  const optionName = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options.data ?? []) map.set(`${o.Kind}:${o.Code}`, o.Name);
    return map;
  }, [options.data]);

  const grouped = useMemo(() => {
    const list = templates.data ?? [];
    return CATEGORIES.map((category) => ({
      category,
      items: list
        .filter((t) => t.Category === category)
        .sort((a, b) => a.SortOrder - b.SortOrder || a.Title.localeCompare(b.Title)),
    })).filter((g) => g.items.length > 0);
  }, [templates.data]);

  if (templates.loading) return <Loading label="Loading the library" />;
  if (templates.error) return <ErrorBanner message={templates.error} />;

  const list = templates.data ?? [];

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="sub">
          {scope === 'active'
            ? 'The tasks an onboarding is assembled from. Editing one here does not change tasks already on a client — those were copied when they were assigned, so wording cannot shift under somebody mid-way through.'
            : 'Retired entries. Nothing new is assigned from these, but the tasks already created from them are untouched and their trigger rules are still here — restoring one puts it back exactly as it was.'}
        </p>
        <div className="row wrap" style={{ gap: 'var(--space-3)' }}>
          <Segmented<Scope>
            label="Library scope"
            value={scope}
            onChange={setScope}
            options={[
              { id: 'active', label: 'Active' },
              { id: 'retired', label: 'Retired' },
            ]}
          />
          {scope === 'active' && (
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              New task
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {list.length === 0 ? (
        <Empty title={scope === 'active' ? 'The library is empty' : 'Nothing has been retired'}>
          <p>
            {scope === 'active'
              ? 'Nothing to assign yet. The starting set ships as a seed — if this is empty, the seed has not been run against this database.'
              : 'Every entry in the library is in use. Retiring one takes it out of everything new without touching the tasks already created from it.'}
          </p>
        </Empty>
      ) : (
        grouped.map((group) => (
          <section key={group.category} className="mb-4">
            <p className="eyebrow">
              <span aria-hidden="true">{CATEGORY_GLYPH[group.category]}</span>{' '}
              {CATEGORY_LABEL[group.category]} · {group.items.length}
            </p>
            <div className="onb-library mt-2">
              {group.items.map((t) => (
                <article key={t.TemplateId} className="onb-lib">
                  <div className="grow">
                    <button
                      type="button"
                      className="onb-lib-title"
                      onClick={() => setEditing(t)}
                    >
                      {t.Title}
                    </button>
                    {t.Description && <p className="small muted mt-2">{t.Description}</p>}
                    <p className="xs subtle mt-2">
                      Due {t.DefaultDueDays}d after assignment
                      {t.RequiresUpload ? ` · collects "${t.UploadLabel || 'a document'}"` : ''}
                      {t.EmailBody ? ' · email template set' : ''}
                      {t.files.length > 0
                        ? ` · email attaches ${t.files.length === 1 ? t.files[0]?.FileName : `${t.files.length} files`}`
                        : ''}
                      {t.InternalNotes ? ' · has internal notes' : ''}
                    </p>
                    <p className="xs subtle mt-2">
                      {t.Triggers.length === 0 ? (
                        'Assigned by hand — no answer pulls this one in.'
                      ) : (
                        <>
                          Pulled in by{' '}
                          {t.Triggers.map(
                            (r) => optionName.get(`${r.Kind}:${r.Value}`) ?? r.Value,
                          ).join(', ')}
                          {t.Triggers.some((r) => r.ByCategory)
                            ? ` (via the ${CATEGORY_LABEL[group.category].toLowerCase()} category)`
                            : ''}
                        </>
                      )}
                    </p>
                  </div>
                  {/* Internal work is never emailed to the client, so it gets no composer. */}
                  {t.IsActive && t.Category !== 'internal' && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setComposing(t)}>
                      Compose email
                    </button>
                  )}
                  {/* Restoring is not destructive and does not ask twice. */}
                  {t.IsActive ? (
                    <DeleteButton
                      what={`the "${t.Title}" library entry`}
                      label="Retire"
                      verb="Retire"
                      consequence="Nothing new is assigned from it. Tasks already on a client are untouched, and you can restore it from the Retired tab."
                      onConfirm={() =>
                        void api
                          .del(`/sales/onboarding/templates/${t.TemplateId}`)
                          .then(() => templates.reload())
                          .catch((err: unknown) =>
                            setError(err instanceof Error ? err.message : 'Could not retire that'),
                          )
                      }
                    />
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        void api
                          .post(`/sales/onboarding/templates/${t.TemplateId}/restore`, {})
                          .then(() => templates.reload())
                          .catch((err: unknown) =>
                            setError(err instanceof Error ? err.message : 'Could not restore that'),
                          )
                      }
                    >
                      Restore
                    </button>
                  )}
                </article>
              ))}
            </div>
          </section>
        ))
      )}

      {composing && <LibraryComposeModal template={composing} onClose={() => setComposing(null)} />}

      {editing && (
        <TemplateDrawer
          template={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onFilesChanged={() => templates.reload()}
          onSaved={() => {
            setEditing(null);
            templates.reload();
          }}
        />
      )}
    </>
  );
}

function TemplateDrawer({
  template,
  onClose,
  onSaved,
  onFilesChanged,
}: {
  template: OnboardingTemplate | null;
  onClose: () => void;
  onSaved: () => void;
  onFilesChanged: () => void;
}) {
  const [form, setForm] = useState({
    ...EMPTY,
    ...(template
      ? {
          title: template.Title,
          description: template.Description ?? '',
          category: template.Category,
          defaultDueDays: template.DefaultDueDays,
          requiresUpload: template.RequiresUpload,
          uploadLabel: template.UploadLabel ?? '',
          emailSubject: template.EmailSubject ?? '',
          emailBody: template.EmailBody ?? '',
          internalNotes: template.InternalNotes ?? '',
          sortOrder: template.SortOrder,
        }
      : {}),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      category: form.category,
      defaultDueDays: form.defaultDueDays,
      requiresUpload: form.requiresUpload,
      uploadLabel: form.uploadLabel.trim() || null,
      emailSubject: form.emailSubject.trim() || null,
      emailBody: form.emailBody.trim() || null,
      internalNotes: form.internalNotes.trim() || null,
      sortOrder: form.sortOrder,
    };
    try {
      if (template) await api.put(`/sales/onboarding/templates/${template.TemplateId}`, payload);
      else await api.post('/sales/onboarding/templates', payload);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={template ? template.Title : 'New library task'}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.title.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="Title" hint="Unique. The trigger rules are matched by it.">
        <input
          className="input"
          value={form.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="e.g. Deploy RMM Agent"
          autoFocus
        />
      </Field>

      <Field label="Description" hint="What has to be done, in enough detail to pick it up cold.">
        <textarea
          className="textarea"
          rows={3}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Field>

      <div className="form-grid">
        <Field label="Category">
          <select
            className="select"
            value={form.category}
            onChange={(e) => set({ category: e.target.value as OnboardingCategory })}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Default due (days)" hint="Counted from the day it is assigned.">
          <input
            className="input"
            type="number"
            min={0}
            max={365}
            value={form.defaultDueDays}
            onChange={(e) => set({ defaultDueDays: Number(e.target.value) })}
          />
        </Field>
      </div>

      <Field label="Order" hint="Within its category, low first.">
        <input
          className="input"
          type="number"
          value={form.sortOrder}
          onChange={(e) => set({ sortOrder: Number(e.target.value) })}
        />
      </Field>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={form.requiresUpload}
          onChange={(e) => set({ requiresUpload: e.target.checked })}
        />
        This task collects a document
      </label>

      {form.requiresUpload && (
        <Field label="What is being collected">
          <input
            className="input"
            value={form.uploadLabel}
            onChange={(e) => set({ uploadLabel: e.target.value })}
            placeholder="e.g. Signed service agreement (PDF)"
          />
        </Field>
      )}

      <Field
        label="Internal notes"
        hint="Never sent, never shown to the client. The step-by-step for whoever picks this up."
      >
        <textarea
          className="textarea"
          rows={4}
          value={form.internalNotes}
          onChange={(e) => set({ internalNotes: e.target.value })}
          placeholder="e.g. RMM console → Devices → Deploy agent, against the client group."
        />
      </Field>

      {/* Offered on any category, not only communication. A setup task often
          needs one mail to ask for access, and the source app's restriction
          meant that mail was retyped every time. */}
      <Field
        label="Email subject"
        hint="Optional. Placeholders: {{Client Name}}, {firstName}, {companyName}."
      >
        <input
          className="input"
          value={form.emailSubject}
          onChange={(e) => set({ emailSubject: e.target.value })}
          placeholder="e.g. Welcome to {companyName} — your onboarding starts now"
        />
      </Field>

      <Field
        label="Email body"
        hint="Filled in and opened in your mail client when the task is composed. Nothing is sent from here."
      >
        <textarea
          className="textarea"
          rows={10}
          value={form.emailBody}
          onChange={(e) => set({ emailBody: e.target.value })}
          placeholder={'Hi {firstName},\n\n…\n\nBest regards'}
        />
      </Field>

      <EmailAttachments template={template} onError={setError} onChanged={onFilesChanged} />

      {form.category === 'internal' && (form.emailSubject || form.emailBody) && (
        <p className="xs subtle">
          This entry is in the internal category, so the composer will not offer it on a task —
          internal work is never emailed to the client.
        </p>
      )}
    </Drawer>
  );
}

/**
 * The files this task's email sends — a blank form the client fills in.
 *
 * Saved the moment they are chosen, not with the drawer's Save: a file is its
 * own row (migration 182), and holding bytes in the form until Save would lose
 * them to a Cancel nobody meant. A new entry has nowhere to keep them yet.
 */
function EmailAttachments({
  template,
  onError,
  onChanged,
}: {
  template: OnboardingTemplate | null;
  onError: (message: string) => void;
  onChanged: () => void;
}) {
  const [files, setFiles] = useState<OnboardingTemplateFile[]>(template?.files ?? []);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const fail = (err: unknown) => onError(err instanceof Error ? err.message : 'Could not do that');

  const upload = async (chosen: File) => {
    if (!template) return;
    setBusy(true);
    try {
      const row = await api.post<OnboardingTemplateFile>(
        `/sales/onboarding/templates/${template.TemplateId}/files`,
        { fileName: chosen.name, content: await readAsBase64(chosen) },
      );
      setFiles((f) => [...f, row]);
      onChanged();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Field
      label="Email attachments"
      hint="Sent with this task's email on every client — a form for them to fill in, say. Replacing it here changes what the next email sends."
    >
      {!template ? (
        <p className="xs subtle">Save the task first, then add files here.</p>
      ) : (
        <div className="onb-files">
          {files.map((f) => (
            <div key={f.FileId} className="onb-file">
              <button type="button" className="link-button" onClick={() => void downloadTemplateFile(f).catch(fail)}>
                {f.FileName}
              </button>
              <span className="subtle xs">{formatBytes(f.SizeBytes)}</span>
              <DeleteButton
                what={f.FileName}
                label="Remove"
                onConfirm={() =>
                  void api
                    .del(`/sales/onboarding/template-files/${f.FileId}`)
                    .then(() => {
                      setFiles((all) => all.filter((x) => x.FileId !== f.FileId));
                      onChanged();
                    })
                    .catch(fail)
                }
              />
            </div>
          ))}
          <input
            ref={input}
            type="file"
            aria-label="Add an email attachment"
            disabled={busy}
            onChange={(e) => {
              const chosen = e.target.files?.[0];
              if (chosen) void upload(chosen);
            }}
          />
        </div>
      )}
    </Field>
  );
}

/**
 * A library task's email, written to one client from here.
 *
 * The handover no longer creates a task board per client — it goes to the
 * account manager by email — so this is where a task's email is sent from:
 * name the contact and the company, and the template fills in around them.
 * With an attachment the message is saved to the sender's Drafts with the file
 * on it (onboardingEmail.ts); without one, a mailto: as everywhere else.
 */
function LibraryComposeModal({ template, onClose }: { template: OnboardingTemplate; onClose: () => void }) {
  const [to, setTo] = useState('');
  const [firstName, setFirstName] = useState('');
  const [companyName, setCompanyName] = useState('');
  /* Null until typed in: the template keeps filling in around the names until
     somebody edits the text itself, and then their words are left alone. */
  const [subjectEdit, setSubjectEdit] = useState<string | null>(null);
  const [bodyEdit, setBodyEdit] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const files = template.files;
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : 'Could not do that');

  const values = { firstName: firstName.trim(), companyName: companyName.trim() };
  const subject = subjectEdit ?? fillPlaceholders(template.EmailSubject || template.Title, values);
  const attached = files.length
    ? `\n\nI have attached ${files.map((f) => f.FileName).join(' and ')} — please fill it in and send it back to me.`
    : '';
  const body =
    bodyEdit ??
    (template.EmailBody
      ? fillPlaceholders(template.EmailBody, values)
      : `Hi ${values.firstName || 'there'},\n\n${template.Description || template.Title}${attached}\n\nLet us know if you have any questions.\n\nBest regards`);

  const [saved, setSaved] = useState<string | null>(null);
  const saveDraft = () => {
    setBusy(true);
    setError(null);
    saveDraftToMailbox(to.trim(), subject, body, files)
      .then(setSaved)
      .catch(fail)
      .finally(() => setBusy(false));
  };

  if (saved) return <DraftSaved title={template.Title} link={saved} onClose={onClose} />;

  return (
    <Modal
      title={`Email — ${template.Title}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <a
            className={files.length ? 'btn btn-ghost' : 'btn btn-primary'}
            href={mailtoHref(to.trim(), subject, body)}
            onClick={onClose}
          >
            {files.length ? 'Open in mail without the file' : 'Open in mail'}
          </a>
          {files.length > 0 && (
            <button type="button" className="btn btn-primary" disabled={busy || !to.trim()} onClick={saveDraft}>
              {busy ? 'Saving…' : 'Save to my Drafts'}
            </button>
          )}
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="To">
        <input
          className="input"
          type="email"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="client.contact@example.com"
          autoFocus
        />
      </Field>
      <div className="form-grid">
        <Field label="Contact first name">
          <input className="input" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </Field>
        <Field label="Company">
          <input className="input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
        </Field>
      </div>
      <Field label="Subject">
        <input className="input" value={subject} onChange={(e) => setSubjectEdit(e.target.value)} />
      </Field>
      <Field label="Message">
        <textarea className="textarea" rows={12} value={body} onChange={(e) => setBodyEdit(e.target.value)} />
      </Field>
      {files.length > 0 && (
        <Field label="Attachments">
          <div className="onb-files">
            {files.map((f) => (
              <div key={f.FileId} className="onb-file">
                <button type="button" className="link-button" onClick={() => void downloadTemplateFile(f).catch(fail)}>
                  {f.FileName}
                </button>
                <span className="subtle xs">{formatBytes(f.SizeBytes)}</span>
              </div>
            ))}
          </div>
        </Field>
      )}
      <p className="xs subtle">
        {files.length
          ? 'Saves the email, addressed and with the file attached, to the Drafts folder of your own mailbox. Open it there, add your signature under the message, and send. The first time, Microsoft asks you to let this app save drafts for you. '
          : 'Opens in your mail client with this filled in. '}
        Nothing is sent from here.
      </p>
    </Modal>
  );
}

/** What the composer becomes once the draft is in the sender's mailbox. */
export function DraftSaved({ title, link, onClose }: { title: string; link: string; onClose: () => void }) {
  return (
    <Modal
      title={`Email — ${title}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Done
          </button>
          <a className="btn btn-primary" href={link} target="_blank" rel="noreferrer" onClick={onClose}>
            Open in Outlook on the web
          </a>
        </>
      }
    >
      <p>
        <strong>Saved to your Drafts.</strong> It is in the Drafts folder in Outlook — the app or the web —
        with the message at the top and the file attached.
      </p>
      <p className="small muted mt-2">
        Open it, put your signature under the message (Insert › Signature), and send. Nothing has been sent yet.
      </p>
    </Modal>
  );
}
